<template>
  <div :class="['search-bar']">
    <button
      class="search-bar__submit"
      :disabled="isLoading || !textInput"
      title="Search"
      aria-label="Search"
      data-test="search-bar-submit"
      @click="$emit('submit-search-clicked')"
    >
      <x-icon
        class="search-bar__submit__icon"
        icon="magnifying-glass"
        :width="iconWidth"
        :height="iconWidth"
        color="#a5a5a5"
      />
    </button>
    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
    <input
      ref="inputRef"
      :value="text"
      title="Search"
      type="text"
      :placeholder="placeholder"
      aria-controls="results-panel"
      aria-autocomplete="list"
      class="search-bar__input"
      data-test="search-bar-input"
      @input="setTextInput"
      @keyup="$emit('input-keyup', $event)"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <button
      v-if="showSearchClear"
      id="search-clear"
      class="search-bar__close"
      title="Clear search"
      aria-label="Clear search"
      data-test="search-bar-clear"
      @click="handleSearchClear"
    >
      <x-icon
        icon="close"
        width="30"
        height="30"
        color="#a5a5a5"
        class="close-icon"
      />
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import {
  isLoading,
  searchIsActive,
  activeLastSearchTerm,
} from '@/services/search-service'
import browser from '@/lib/browser'
import xIcon from '@/components/shared/x-icon/index.vue'

const emit = defineEmits([
  'submit-search-clicked',
  'input-keyup',
  'search-focus',
  'reset-search',
])

interface SearchBarProps {
  textInput?: string
  placeholder?: string
}

const { textInput = '', placeholder = 'Search' } = defineProps<SearchBarProps>()

const route = useRoute()

const inputRef = ref<HTMLInputElement | null>(null)
const text = ref(textInput.length ? textInput : '')
const isFocused = ref(false)

const isMobile = computed(() => browser.isiPhone() || browser.isAndroid())

const iconWidth = computed(() => {
  return !isMobile.value ? '20' : '16'
})

const isQuickSearchRoute = computed(() => route.path.includes('quick-search'))

const showSearchClear = computed(() => {
  return (
    isFocused.value ||
    searchIsActive() ||
    activeLastSearchTerm.value ||
    isQuickSearchRoute.value
  )
})

watch(
  () => textInput,
  (input) => {
    // Need this extra set of putting the computed prop into data, to use for the input :value to work
    text.value = input
  }
)

const setTextInput = (e: Event) => {
  const target = e.target as HTMLInputElement
  text.value = target.value
}

const autoFocus = () => {
  inputRef.value?.focus()
}

const handleFocus = () => {
  emit('search-focus')
  isFocused.value = true
}

const handleBlur = () => {
  isFocused.value = false
}

const handleSearchClear = () => {
  emit('reset-search')
  inputRef.value?.blur()
}
defineExpose({ autoFocus })
</script>

<style lang="scss" scoped>
@use '@/styles/system/_variables.scss';
@use '@/styles/system/_shadow.scss';

$search-bar-height: 45px;

.search-bar {
  display: flex;
  align-items: center;
  height: $search-bar-height;
  background-color: var(--ys-color-background-primary);
  border-radius: variables.$border-radius-lg;

  &__submit {
    height: $search-bar-height;
    padding-left: 12px;
    padding-right: 12px;
    background: transparent;
    border-radius: 0;

    &:hover,
    &:focus {
      background: transparent;
      outline: none;
    }
    &:disabled {
      cursor: default;
    }

    &__icon {
      position: relative;
      top: 1px;
    }
  }

  &__input {
    font-size: 14px;
    margin: 0;
    padding-left: 0;
    padding-right: 0;
    border: 0;
    box-shadow: none;
    background-color: transparent;

    &:focus {
      border: 0;
      box-shadow: none;
      outline: none;
    }

    &::placeholder {
      color: var(--ys-color-text-disabled);
    }
  }

  &__close {
    padding-left: 10px;
    padding-right: 10px;
    margin-top: 1px;
    background-color: transparent;
    border: none;

    &:deep(path) {
      fill: var(--ys-color-stroke-base);
    }
    &:hover,
    &:focus {
      outline: none;
      &:deep(path) {
        fill: var(--ys-color-icon-primary);
      }
    }
  }
}
</style>
